import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Mail, Phone, MailCheck, MailWarning, Package } from "lucide-react";

export default function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = (term) => api.get("/admin/customers", { params: { q: term || undefined } }).then((r) => setCustomers(r.data));
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q]);

  const open = (id) => api.get(`/admin/customers/${id}`).then((r) => setDetail(r.data));

  return (
    <div>
      <h1 className="text-3xl font-extrabold mb-1">Customers</h1>
      <p className="text-muted-foreground mb-6">Registered accounts with their linked order history and enquiries.</p>
      <div className="relative max-w-md mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input data-testid="admin-customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone or company…" className="pl-9" />
      </div>
      <div className="rounded-xl border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th><th className="p-3 text-center">Verified</th><th className="p-3 text-center">Orders</th><th className="p-3 text-right">Spend</th><th className="p-3"></th></tr>
          </thead>
          <tbody className="divide-y">
            {customers.map((c) => (
              <tr key={c.id} data-testid={`admin-customer-${c.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => open(c.id)}>
                <td className="p-3 font-medium">{c.name || "—"}</td>
                <td className="p-3">{c.email}</td>
                <td className="p-3 text-muted-foreground">{c.phone || "—"}</td>
                <td className="p-3 text-center">{c.email_verified ? <MailCheck className="inline text-emerald-600" size={16} /> : <MailWarning className="inline text-amber-500" size={16} />}</td>
                <td className="p-3 text-center">{c.order_count}</td>
                <td className="p-3 text-right font-mono">{money(c.total_spend)}</td>
                <td className="p-3 text-right"><span className="text-accent font-semibold">View →</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {customers.length === 0 && <p className="p-6 text-center text-muted-foreground">No customer accounts yet.</p>}
      </div>

      {detail && (
        <Dialog open onOpenChange={() => setDetail(null)}>
          <DialogContent data-testid="customer-detail-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{detail.customer.name || detail.customer.email}</DialogTitle></DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="flex flex-wrap gap-4 text-muted-foreground">
                <span className="flex items-center gap-1.5"><Mail size={14} /> {detail.customer.email}</span>
                {detail.customer.phone && <span className="flex items-center gap-1.5"><Phone size={14} /> {detail.customer.phone}</span>}
                <Badge className={detail.customer.email_verified ? "bg-emerald-600" : "bg-amber-500"}>{detail.customer.email_verified ? "Verified" : "Unverified"}</Badge>
                <Badge variant="outline">{detail.customer.auth_provider}</Badge>
              </div>
              {detail.customer.company && <div><strong>Company:</strong> {detail.customer.company}</div>}
              {detail.customer.saved_address?.line1 && (
                <div><strong>Saved address:</strong> {[detail.customer.saved_address.line1, detail.customer.saved_address.city, detail.customer.saved_address.postcode, detail.customer.saved_address.country].filter(Boolean).join(", ")}</div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground uppercase">Orders</div><div className="text-xl font-bold">{detail.customer.order_count}</div></div>
                <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground uppercase">Total spend</div><div className="text-xl font-bold text-accent">{money(detail.customer.total_spend)}</div></div>
              </div>

              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2"><Package size={15} /> Order history</h3>
                {detail.orders.length === 0 ? <p className="text-muted-foreground">No orders.</p> : (
                  <div className="space-y-2">
                    {detail.orders.map((o) => (
                      <div key={o.order_number} className="flex items-center justify-between rounded-lg border p-3">
                        <div><span className="font-mono font-semibold">{o.order_number}</span><span className="text-xs text-muted-foreground ml-2">{new Date(o.created_at).toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland" })}</span></div>
                        <div className="flex items-center gap-3"><Badge className="bg-slate-600 capitalize">{o.status_label}</Badge><span className="font-mono font-bold">{money(o.total)}</span></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {detail.enquiries.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Enquiries & quotes</h3>
                  <div className="space-y-2">
                    {detail.enquiries.map((e) => (
                      <div key={e.id} className="rounded-lg border p-3">
                        <div className="flex justify-between"><span className="font-medium">{e.product_name || e.sku || "General"}</span><span className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland" })}</span></div>
                        {e.message && <p className="text-muted-foreground mt-1">{e.message}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
